import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';

import {
    selectGuardianInfo,
    selectLegalUpdating,
    selectLegalLoading,
    selectDuplicatedEmail
} from '../selectors/legal.selector';
import { updateGuardianInfo, fetchGuardianInfo } from '../actions/legal.actions';
import { GuardianInfo } from '../types/legal.types';
import { createSimpleGuardianValidationSchema } from '../utils/guardianValidation';
import { selectAccountEmail } from '../selectors/account.selector';
import { resetDuplicatedEmail } from '../reducer/legal.reducer';

export const useGuardianForm = () => {
    const dispatch = useDispatch();
    const guardianInfo = useSelector(selectGuardianInfo);
    const updating = useSelector(selectLegalUpdating);
    const loading = useSelector(selectLegalLoading);
    const duplicatedEmail = useSelector(selectDuplicatedEmail);
    const accountEmail = useSelector(selectAccountEmail);
    const { t } = useTranslation();

    useEffect(() => {
        dispatch(fetchGuardianInfo() as any);
    }, []);

    const validationSchema = createSimpleGuardianValidationSchema({ t, accountEmail });

    const formik = useFormik({
        initialValues: {
            guardianFirstName: guardianInfo?.guardianFirstName || '',
            guardianLastName: guardianInfo?.guardianLastName || '',
            guardianPhoneNumber: guardianInfo?.guardianPhoneNumber || '',
            guardianEmail: guardianInfo?.guardianEmail || ''
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            const guardianData: GuardianInfo = {
                guardianFirstName: values.guardianFirstName,
                guardianLastName: values.guardianLastName,
                guardianPhoneNumber: values.guardianPhoneNumber,
                guardianEmail: values.guardianEmail
            };
            // @ts-ignore
            await dispatch(updateGuardianInfo(guardianData));
        }
    });

    const handleChangeFormValues = useCallback(
        (_: string, evt: any) => {
            if (evt?.target?.name === 'guardianEmail' && duplicatedEmail) {
                dispatch(resetDuplicatedEmail());
            }
            formik.handleChange(evt);
            formik.setFieldTouched(evt.target.name, true, false);
        },
        [formik]
    );

    return {
        formik,
        handleChangeFormValues,
        updating,
        loading,
        duplicatedEmail
    };
};
